import type { AdminUserDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Orders tab of the user detail drawer (AUT-124). Reads the rollup
 * straight off the detail DTO (`ordersSummary.{total,byStatus}`) — no
 * extra fetch (the full per-order list is an ADMIN-15+ surface).
 */
export const OrdersTab = ({ user }: { user: AdminUserDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const byStatus = Object.entries(user.ordersSummary.byStatus);

  if (user.ordersSummary.total === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('users.detail.ordersTab.empty')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2">
        {t('users.detail.ordersTab.total', { count: user.ordersSummary.total })}
      </Typography>
      {byStatus.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('users.detail.ordersTab.byStatus')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
            {byStatus.map(([status, count]) => (
              <Chip key={status} size="small" variant="outlined" label={`${status}: ${count}`} />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
